import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
// import { useApi } from '../../hooks/useApi';
import { Customer } from '../../types/customerTypes';
import customerService from '../../services/customerService';
import Button from '../../components/common/Button/Button';
import Modal from '../../components/common/Modal/Modal';
import CustomerForm from '../../components/Customers/CustomerForm';
import styles from './CustomerList.module.scss';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const CustomerList: React.FC = () => {
  const { auth } = useAuth();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredCustomers, setFilteredCustomers] = useState<Customer[]>([]);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [formLoading, setFormLoading] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState<Customer | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Check if user has customer management permissions
  const canManageCustomers = auth.user?.role === 'admin' || 
    auth.user?.permissions?.canManageCustomers;

  // Mock data for development
  useEffect(() => {
    loadCustomers();
  }, []);

  const loadCustomers = async () => {
    setLoading(true);
    try {
      const customers = await customerService.getCustomers();
      setCustomers(Array.isArray(customers) ? customers : []);
      setFilteredCustomers(Array.isArray(customers) ? customers : []);
    } catch (error) {
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  // Filter customers based on search term
  useEffect(() => {
    const filtered = customers.filter(customer =>
      customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.phone.includes(searchTerm)
    );
    setFilteredCustomers(filtered);
  }, [searchTerm, customers]);

  // const handleDelete = async (customerId: string) => {
  //   if (window.confirm('Are you sure you want to delete this customer?')) {
  //     try {
  //       await customerService.deleteCustomer(customerId);
  //       toast.success('Customer deleted successfully!');
  //       // Optionally, refresh the list from the server:
  //       // await loadCustomers();
  //       // Or, remove from local state:
  //       setCustomers(prev => prev.filter(c => c.id !== customerId));
  //       setFilteredCustomers(prev => prev.filter(c => c.id !== customerId));
  //     } catch (error) {
  //       toast.error('Failed to delete customer');
  //       console.error('Failed to delete customer:', error);
  //     }
  //   }
  // };

  const downloadCSV = () => {
    const headers = ['Name', 'Email', 'Phone', 'Address', 'Location'];
    const csvContent = [
      headers.join(','),
      ...filteredCustomers.map(customer => [
        customer.name,
        customer.email,
        customer.phone,
        customer.address,
        `${customer.city} ${customer.state} ${customer.zip_code}`
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `customers_${new Date().toLocaleDateString('en-CA')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadPDF = () => {
    // Create HTML content for PDF
    const htmlContent = `
      <html>
        <head>
          <title>Customers Report</title>
          <style>
            @page {
              margin: 20mm;
              size: A4;
            }
            body { 
              font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; 
              margin: 0; 
              padding: 0;
              color: #2c3e50;
              background: white;
            }
            .container {
              max-width: 100%;
              margin: 0 auto;
              padding: 30px;
            }
            .header { 
              text-align: center;
              margin-bottom: 30px; 
              padding: 20px 0;
              border-bottom: 3px solid #3498db;
            }
            .company-title {
              font-size: 28px;
              font-weight: bold;
              color: #2c3e50;
              margin: 0 0 10px 0;
            }
            .report-title {
              font-size: 20px;
              color: #7f8c8d;
              margin: 0 0 15px 0;
              font-weight: 500;
            }
            .header-info {
              display: flex;
              justify-content: space-between;
              align-items: center;
              margin-bottom: 20px;
              font-size: 14px;
              color: #7f8c8d;
            }
            .date-info {
              background: #ecf0f1;
              padding: 8px 15px;
              border-radius: 5px;
              font-weight: 500;
            }
            .stats-info {
              background: #3498db;
              color: white;
              padding: 8px 15px;
              border-radius: 5px;
              font-weight: 500;
            }
            table { 
              width: 100%; 
              border-collapse: collapse; 
              margin-top: 20px;
              box-shadow: 0 2px 8px rgba(0,0,0,0.1);
            }
            th { 
              background: linear-gradient(135deg, #3498db, #2980b9);
              color: white;
              font-weight: 600;
              padding: 15px 12px;
              text-align: left;
              font-size: 14px;
              text-transform: uppercase;
              letter-spacing: 0.5px;
            }
            td { 
              padding: 12px;
              border-bottom: 1px solid #ecf0f1;
              font-size: 14px;
            }
            tr:nth-child(even) {
              background-color: #f8f9fa;
            }
            tr:hover {
              background-color: #e8f4fd;
            }
            .customer-name {
              font-weight: 600;
              color: #2c3e50;
            }
            .customer-email {
              color: #3498db;
            }
            .customer-phone {
              color: #7f8c8d;
            }
            .customer-location {
              color: #27ae60;
              font-weight: 500;
            }
            .footer {
              margin-top: 30px;
              text-align: center;
              color: #7f8c8d;
              font-size: 12px;
              border-top: 1px solid #ecf0f1;
              padding-top: 15px;
            }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="header">
              <div class="company-title">INVOICE MANAGEMENT SYSTEM</div>
              <div class="report-title">Customers Report</div>
            </div>
            
            <div class="header-info">
              <div class="date-info">
                Generated on: ${new Date().toLocaleDateString('en-CA', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </div>
              <div class="stats-info">
                Total Customers: ${filteredCustomers.length}
              </div>
            </div>
            
            <table>
              <thead>
                <tr>
                  <th>Customer Name</th>
                  <th>Email Address</th>
                  <th>Phone Number</th>
                  <th>Location</th>
                </tr>
              </thead>
              <tbody>
                ${filteredCustomers.map(customer => `
                  <tr>
                    <td class="customer-name">${customer.name}</td>
                    <td class="customer-email">${customer.email}</td>
                    <td class="customer-phone">${customer.phone}</td>
                    <td class="customer-location">${customer.city}, ${customer.state}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
            
            <div class="footer">
              <p>This report was generated automatically by the Invoice Management System</p>
              <p>© ${new Date().getFullYear()} Invoice Management System. All rights reserved.</p>
            </div>
          </div>
        </body>
      </html>
    `;

    // Convert HTML to PDF using jsPDF and html2canvas
    const { jsPDF } = require('jspdf');
    const html2canvas = require('html2canvas');

    // Create a temporary div to render the HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;
    tempDiv.style.position = 'absolute';
    tempDiv.style.left = '-9999px';
    tempDiv.style.top = '0';
    document.body.appendChild(tempDiv);

    // Convert to canvas and then to PDF
    html2canvas(tempDiv, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff'
    }).then((canvas: any) => {
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgWidth = 210;
      const pageHeight = 295;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;

      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      // Download the PDF
      pdf.save(`customers_${new Date().toLocaleDateString('en-CA')}.pdf`);
      
      // Clean up
      document.body.removeChild(tempDiv);
    }).catch((error: any) => {
      console.error('Error generating PDF:', error);
      // Fallback to print method if PDF generation fails
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(htmlContent);
        printWindow.document.close();
        printWindow.print();
      }
      document.body.removeChild(tempDiv);
    });
  };

  const printCustomers = () => {
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>Customers List</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 20px; }
              table { width: 100%; border-collapse: collapse; margin-top: 20px; }
              th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
              th { background-color: #f2f2f2; font-weight: bold; }
              h1 { color: #333; }
              .header { margin-bottom: 20px; }
              .date { color: #666; font-size: 14px; }
            </style>
          </head>
          <body>
            <div class="header">
              <h1>Customers List</h1>
              <div class="date">Printed on: ${new Date().toLocaleDateString()}</div>
            </div>
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Location</th>
                </tr>
              </thead>
              <tbody>
                ${filteredCustomers.map(customer => `
                  <tr>
                    <td>${customer.name}</td>
                    <td>${customer.email}</td>
                    <td>${customer.phone}</td>
                    <td>${customer.city}, ${customer.state}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  };

  const handleCreateCustomer = () => {
    setIsCreateModalOpen(true);
  };

  const handleEditCustomer = (customer: Customer) => {
    setEditingCustomer(customer);
    setIsEditModalOpen(true);
  };

  const handleSubmitCustomer = async (customerData: Customer) => {
    setFormLoading(true);
    try {
      await customerService.createCustomer(customerData);
      toast.success('Customer created successfully!');
      setIsCreateModalOpen(false);
      // Refresh the list
      loadCustomers();
    } catch (error) {
      toast.error('Failed to create customer');
    } finally {
      setFormLoading(false);
    }
  };

  const handleEditCustomerSubmit = async (customerData: Customer) => {
    setFormLoading(true);
    try {
      await customerService.updateCustomer(customerData);
      toast.success('Customer created successfully!');
      setIsEditModalOpen(false);
      // Refresh the list
      loadCustomers();
    } catch (error) {
      toast.error('Failed to create customer');
    } finally {
      setFormLoading(false);
    }
  };

  const handleCloseModal = () => {
    setIsCreateModalOpen(false);
    setIsEditModalOpen(false);
    setEditingCustomer(null);
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner}></div>
        <p>Loading customers...</p>
      </div>
    );
  }

  return (
    <div className={styles.customerList}>
      <div className={styles.header}>
        <div className={styles.titleSection}>
          <h2>Customers</h2>
          <p>Manage your customer information</p>
        </div>
        {canManageCustomers && (
          <Button
            variant="primary"
            onClick={handleCreateCustomer}
          >
            Add Customer
          </Button>
        )}
      </div>

      <div className={styles.controls}>
        <div className={styles.searchContainer}>
            <div className={styles.searchWrapper}>
              <svg className={styles.searchIcon} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
              </svg>
              <input
                placeholder="Search customers by name, email, or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={styles.searchInput}
              />
              {searchTerm && (
                <button
                  className={styles.clearButton}
                  onClick={() => setSearchTerm('')}
                  aria-label="Clear search"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              )}
            </div>
          </div>

        <div className={styles.actionsSection}>
          <div className={styles.stats}>
            <span>Total: {Array.isArray(customers) ? customers.length : 0}</span>
          </div>
          <div className={styles.downloadActions}>
            <button
              className={styles.downloadButton}
              onClick={downloadCSV}
              title="Download CSV"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="7,10 12,15 17,10"></polyline>
                <line x1="12" y1="15" x2="12" y2="3"></line>
              </svg>
              CSV
            </button>
            <button
              className={styles.downloadButton}
              onClick={downloadPDF}
              title="Download PDF"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                <polyline points="14,2 14,8 20,8"></polyline>
                <line x1="16" y1="13" x2="8" y2="13"></line>
                <line x1="16" y1="17" x2="8" y2="17"></line>
                <polyline points="10,9 9,9 8,9"></polyline>
              </svg>
              PDF
            </button>
            <button
              className={styles.downloadButton}
              onClick={printCustomers}
              title="Print Customers"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="6,9 6,2 18,2 18,9"></polyline>
                <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
                <rect x="6" y="14" width="12" height="8"></rect>
              </svg>
              Print
            </button>
          </div>
        </div>
      </div>

      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Name</th>
              {/* <th>Email</th> */}
              <th>Phone</th>
              <th>Password</th>
              {/* <th>Location</th> */}
              <th>Created Name</th>
              {canManageCustomers && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {filteredCustomers.length === 0 ? (
              <tr>
                <td colSpan={canManageCustomers ? 5 : 4} className={styles.emptyState}>
                  {searchTerm ? 'No customers found matching your search.' : 'No customers found.'}
                </td>
              </tr>
            ) : (
              filteredCustomers.map(customer => (
                <tr key={customer.id}>
                  <td>
                    <div className={styles.customerName}>
                      <span className={styles.name}>{customer.name}</span>
                    </div>
                  </td>
                  {/* <td>{customer.email || '-'}</td> */}
                  <td>{customer.phone}</td>
                  <td>{customer.pwd}</td>
                  {/* <td>
                    {customer.city}, {customer.state}
                  </td> */}
                  <td>
                    {customer.created_by || '-'}
                  </td>
                  {canManageCustomers && (
                    <td>
                      <div className={styles.actions}>
                        <button
                          className={styles.actionButton}
                          onClick={() => navigate(`/customers/${customer.id}`)}
                          title="View Customer"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                            <circle cx="12" cy="12" r="3"></circle>
                          </svg>
                        </button>
                        {customer.is_old_url && customer.is_old_url.trim() !== '' && (
                          <button
                            className={styles.actionButton}
                            onClick={() => window.open(customer.is_old_url, '_blank', 'noopener,noreferrer')}
                            title="Open PDF URL"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M18 13v6a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                              <polyline points="15 3 21 3 21 9"></polyline>
                              <line x1="10" y1="14" x2="21" y2="3"></line>
                            </svg>
                          </button>
                        )}
                        <button
                          className={styles.actionButton}
                          onClick={() => handleEditCustomer(customer)}
                          title="Edit Customer"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                            <path d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                          </svg>
                        </button>
                        <button
                          className={styles.actionButton}
                          onClick={() => {
                            setCustomerToDelete(customer);
                            setDeleteModalOpen(true);
                          }}
                          title="Delete Customer"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="3,6 5,6 21,6"></polyline>
                            <path d="m19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2"></path>
                            <line x1="10" y1="11" x2="10" y2="17"></line>
                            <line x1="14" y1="11" x2="14" y2="17"></line>
                          </svg>
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Create Customer Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={handleCloseModal}
        title="Add Customer"
        size="xl"
      >
        <CustomerForm
          onSubmit={handleSubmitCustomer}
          onCancel={handleCloseModal}
          loading={formLoading}
        />
      </Modal>

      {/* Edit Customer Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={handleCloseModal}
        title="Edit Customer"
        size="xl"
      >
        <CustomerForm
          customer={editingCustomer || undefined}
          onSubmit={handleEditCustomerSubmit}
          onCancel={handleCloseModal}
          loading={formLoading}
        />
      </Modal>

      {/* Delete Customer Modal */}
      <Modal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Delete Customer"
      >
        <p>Are you sure you want to delete <strong>{customerToDelete?.name}</strong>?</p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button variant="secondary" onClick={() => setDeleteModalOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={async () => {
              if (customerToDelete) {
                setDeleting(true);
                try {
                  await customerService.deleteCustomer(customerToDelete.id);
                  toast.success('Customer deleted successfully!');
                  setCustomers(prev => prev.filter(c => c.id !== customerToDelete.id));
                  setFilteredCustomers(prev => prev.filter(c => c.id !== customerToDelete.id));
                } catch (error) {
                  toast.error('Failed to delete customer');
                } finally {
                  setDeleteModalOpen(false);
                  setCustomerToDelete(null);
                  setDeleting(false);
                }
              }
            }}
            disabled={deleting}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </div>
      </Modal>
      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default CustomerList; 
